<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { ref, computed, getCurrentInstance, reactive } from 'vue';
import { useCoinStore } from '@/stores/coin';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useToast } from 'primevue/usetoast';
import { useLoading } from '@/composables/useLoading';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

// Mock coinStore para evitar erro do Pinia
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const { invoice, coins } = defineProps({
    coins: Object,
    invoice: Object,
});

const form = useForm({
    _method: 'PUT',
    value: invoice.value,
    value_coin: invoice.value_coin || 'USD',
    discount: invoice.discount,
    discount_coin: invoice.discount_coin || 'USD',
    paid: invoice.paid || false,
    bank: invoice.bank,
    payment_link: invoice.payment_link,
    file: null,
});

// File upload ref
const fileUploader = ref(null);
const uploadedFile = ref(null);

// Banks options
const bankOptions = [
    { label: 'Mercury', value: 'MERCURY' },
    { label: 'Paypal', value: 'PAYPAL' },
];

// Currency options
const currencyOptions = [
    { label: 'BRL', value: 'BRL' },
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' },
];

// Format currency
const formatCurrency = (value, currency) => {
    if (!value || !currency) return '0.00';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(parseFloat(value) || 0);
};

// Upload handler
const onFileSelect = (event) => {
    const files = event.files;
    if (files && files.length > 0) {
        uploadedFile.value = files[0];
        form.file = files[0];
    }
};

// Submit form
const submit = () => {
    form.post(route('invoices.update', invoice.id), {
        forceFormData: true,
        preserveScroll: true,
        onStart: () => showLoading('Updating invoice...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({ 
                severity: 'success', 
                summary: $t('Success'), 
                detail: $t('Invoice updated successfully'), 
                life: 3000 
            });
        },
        onError(error) {
            console.log(error);
            toast.add({ 
                severity: 'error', 
                summary: $t('Error'), 
                detail: $t('Failed to update invoice'), 
                life: 3000 
            });
        }
    });
};

// Generate invoice
const generateInvoice = () => {
    window.open(route('invoices.generate', invoice.id), '_blank');
};
</script>

<template>
    <div class="grid">
        <Card>
            <template #header>
                <div class="flex items-center justify-between p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <i class="pi pi-pencil text-2xl text-surface-500" />
                        <div>
                            <h2 class="text-xl font-bold">{{ $t('Edit invoice') }}</h2>
                            <span class="text-sm text-surface-500">#{{ invoice.number }}</span>
                        </div>
                    </div>
                </div>
            </template>

            <template #content>
                <form @submit.prevent="submit" class="space-y-6">
                    <!-- Invoice Info -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <!-- Created Date and Orders -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Created at') }}
                            </label>
                            <div class="text-surface-900">
                                {{ new Date(invoice.created_at).toLocaleString() }}
                            </div>
                            
                            <div v-if="invoice.orders && invoice.orders.length > 0" class="mt-2 flex flex-wrap gap-2">
                                <Button
                                    v-for="order in invoice.orders"
                                    :key="order.id"
                                    :label="`#${order.number}`"
                                    link
                                    size="small"
                                    icon="pi pi-external-link"
                                    iconPos="right"
                                    @click="() => {
                                        window.open(route('orders.edit', order.id), '_blank');
                                    }"
                                />
                            </div>
                        </div>

                        <!-- Calculated Value -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Valor dos pedidos') }}
                            </label>
                            <div class="text-surface-900 font-semibold">
                                {{ invoice.calculated_value || formatCurrency(0, 'USD') }}
                            </div>
                        </div>
                    </div>

                    <!-- Value and Discount -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <!-- Value -->
                        <div>
                            <label for="value" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Valor') }}
                            </label>
                            <div class="flex gap-2">
                                <InputNumber
                                    v-model="form.value"
                                    id="value"
                                    :currency="form.value_coin"
                                    mode="currency"
                                    locale="en-US"
                                    :minFractionDigits="2"
                                    :maxFractionDigits="2"
                                    class="flex-1"
                                />
                                <Select
                                    v-model="form.value_coin"
                                    :options="currencyOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-28"
                                />
                            </div>
                            <small v-if="form.errors.value" class="text-red-600">{{ form.errors.value }}</small>
                        </div>

                        <!-- Discount -->
                        <div>
                            <label for="discount" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Desconto') }}
                            </label>
                            <div class="flex gap-2">
                                <InputNumber
                                    v-model="form.discount"
                                    id="discount"
                                    :currency="form.discount_coin"
                                    mode="currency"
                                    locale="en-US"
                                    :minFractionDigits="2"
                                    :maxFractionDigits="2"
                                    class="flex-1"
                                />
                                <Select
                                    v-model="form.discount_coin"
                                    :options="currencyOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    class="w-28"
                                />
                            </div>
                            <small v-if="form.errors.discount" class="text-red-600">{{ form.errors.discount }}</small>
                        </div>
                    </div>

                    <!-- Payment Info -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <!-- Paid Status -->
                        <div>
                            <label class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Payment Status') }}
                            </label>
                            <div class="flex items-center gap-2">
                                <Checkbox
                                    v-model="form.paid"
                                    id="paid"
                                    binary
                                />
                                <label for="paid" class="cursor-pointer">{{ $t('Paid') }}</label>
                            </div>
                            <small v-if="form.errors.paid" class="text-red-600">{{ form.errors.paid }}</small>
                        </div>

                        <!-- Bank -->
                        <div>
                            <label for="bank" class="block text-sm font-medium text-surface-700 mb-2">
                                {{ $t('Banco') }}
                            </label>
                            <Select
                                v-model="form.bank"
                                id="bank"
                                :options="bankOptions"
                                optionLabel="label"
                                optionValue="value"
                                :placeholder="$t('Select...')"
                                class="w-full"
                                showClear
                            />
                            <small v-if="form.errors.bank" class="text-red-600">{{ form.errors.bank }}</small>
                        </div>
                    </div>

                    <!-- Payment Link -->
                    <div>
                        <label for="payment_link" class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Link de pagamento') }}
                        </label>
                        <InputText
                            v-model="form.payment_link"
                            id="payment_link"
                            type="url"
                            class="w-full"
                            :placeholder="$t('https://...')"
                        />
                        <small v-if="form.errors.payment_link" class="text-red-600">{{ form.errors.payment_link }}</small>
                    </div>

                    <!-- File Upload -->
                    <div>
                        <label class="block text-sm font-medium text-surface-700 mb-2">
                            {{ $t('Arquivo') }}
                        </label>
                        <FileUpload
                            ref="fileUploader"
                            mode="basic"
                            name="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            :maxFileSize="10000000"
                            @select="onFileSelect"
                            :auto="false"
                            :chooseLabel="$t('Choose File')"
                        />
                        <small v-if="form.errors.file" class="text-red-600">{{ form.errors.file }}</small>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                        <Button
                            type="button"
                            :label="$t('Generate')"
                            icon="pi pi-file-pdf"
                            severity="secondary"
                            @click="generateInvoice"
                        />
                        
                        <Button
                            type="submit"
                            :label="$t('Save')"
                            icon="pi pi-save"
                            :loading="form.processing"
                            :disabled="form.processing"
                        />

                        <Button
                            type="button"
                            :label="$t('Back')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            class="sm:ml-auto"
                            @click="() => router.visit(route('invoices.index'), {
                                onStart: () => showLoading('Loading invoices...'),
                                onFinish: () => hideLoading()
                            })"
                        />
                    </div>
                </form>
            </template>
        </Card>
    </div>
</template>

<style scoped>
/* Custom styles if needed */
</style>